import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSurface, type Surface, type SurfaceSource } from '@/contexts/SurfaceContext';
import type { SurfaceResolution } from '@/config/surfaceDetection';
import { isPreviewDeploymentHost } from '@/utils/deploymentHost';
import { MOBILE_MAX_WIDTH, readIsMobileLayout } from '@/utils/isMobileLayout';
import {
  applyUiScale,
  computeUiScale,
  detectDeviceClass,
  deviceModeFor,
  isScaleOverrideAllowed,
  readDeviceOverride,
  readScaleOverride,
  screenSizeFor,
  type DeviceInput,
  type DeviceMode,
  type UiLayout,
  type UiScaleOverrides,
  type UiScaleState,
} from '@/config/uiScale';
import { isDeviceOverrideAllowed } from '@/config/uiScaleEnv';

export type { DeviceMode } from '@/config/uiScale';

const DeviceModeContext = createContext<DeviceMode | null>(null);
const UiScaleContext = createContext<UiScaleState | null>(null);

/** The class inputs plus what tells a real resize or rotation from the on-screen keyboard. */
interface ViewportReading extends Omit<DeviceInput, 'kioskDomain'> {
  outerWidth: number;
  outerHeight: number;
  /** `screen.orientation.type` ('' when the browser has none). */
  orientationType: string;
}

const COARSE_POINTER_QUERY = '(pointer: coarse)';

/**
 * The tablet test's `touch`: the primary pointer is a finger. A touch-screen laptop has touch
 * points (isTouchCapable) but a fine primary pointer, so it stays a desktop. The phone /
 * "Desktop site" check (readIsMobileLayout) keeps using isTouchCapable.
 */
function isPrimaryPointerCoarse(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia(COARSE_POINTER_QUERY).matches;
  } catch {
    return false;
  }
}

/** Window, screen (in its current orientation), phone and primary-pointer readings. */
function readViewport(): ViewportReading {
  const scr = typeof screen !== 'undefined' ? screen : undefined;
  const orientationType = scr?.orientation?.type ?? '';
  const { width: screenWidth, height: screenHeight } = screenSizeFor(
    scr?.width ?? 0,
    scr?.height ?? 0,
    orientationType || undefined
  );
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    outerWidth: window.outerWidth || 0,
    outerHeight: window.outerHeight || 0,
    screenWidth,
    screenHeight,
    orientationType,
    isPhone: readIsMobileLayout(),
    touch: isPrimaryPointerCoarse(),
  };
}

function sameViewport(a: ViewportReading, b: ViewportReading): boolean {
  return (
    a.width === b.width &&
    a.height === b.height &&
    a.outerWidth === b.outerWidth &&
    a.outerHeight === b.outerHeight &&
    a.screenWidth === b.screenWidth &&
    a.screenHeight === b.screenHeight &&
    a.orientationType === b.orientationType &&
    a.isPhone === b.isPhone &&
    a.touch === b.touch
  );
}

/**
 * Only the window height changed (inner or outer): the on-screen keyboard (Android WebView
 * adjust-resize, kiosks), not a resize or a rotation. Width, outer width, screen size and
 * orientation are all the same.
 */
function isHeightOnlyChange(prev: ViewportReading, next: ViewportReading): boolean {
  return (
    prev.width === next.width &&
    prev.outerWidth === next.outerWidth &&
    prev.screenWidth === next.screenWidth &&
    prev.screenHeight === next.screenHeight &&
    prev.orientationType === next.orientationType &&
    (prev.height !== next.height || prev.outerHeight !== next.outerHeight)
  );
}

/**
 * Next viewport state. readIsMobileLayout() reads min(outerWidth, outerHeight), so a keyboard
 * that shrinks the window height would turn a landscape kiosk (1.25) or an Android tablet
 * (1.15) into a phone (1.0, compact) while typing. On a height-only change the previous phone
 * and pointer readings stay; the heights (designHeight) still update.
 */
function nextViewportReading(prev: ViewportReading, read: ViewportReading): ViewportReading {
  const next = isHeightOnlyChange(prev, read) ? { ...read, isPhone: prev.isPhone, touch: prev.touch } : read;
  return sameViewport(prev, next) ? prev : next;
}

/**
 * Kiosk hardware layout: the kiosk surface (src/contexts/SurfaceContext.tsx), but never on a Pages
 * preview host (QA tests phone/desktop there) unless a dev/preview `?surface=kiosk` asks for it.
 */
function isKioskHardware(surface: Surface, source: SurfaceSource): boolean {
  return surface === 'kiosk' && (source === 'override' || !isPreviewDeploymentHost());
}

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The page-load URL query: `?ui-scale=` and `?device=` are read from it, not from later routes. */
function readSearch(): string {
  return globalThis.location?.search ?? '';
}

/**
 * `?ui-scale=` applies (and is stored) only on the kiosk class or in a dev/preview build. The
 * class comes from the dev `?device=` or detection; the scale never feeds back into it.
 */
function scaleOverrideAllowedFor(input: DeviceInput, deviceOverride: UiScaleOverrides['deviceOverride']): boolean {
  return isScaleOverrideAllowed(deviceOverride ?? detectDeviceClass(input), isDeviceOverrideAllowed());
}

/**
 * Legacy `body[data-mode]` and the bottom tab bar height. BottomNav shows on the compact layout
 * (phones, iPad portrait, portrait kiosk), so the bar height follows the layout: 3.5rem of the
 * scaled root (56px at scale 1), else 0. It lives on <html> because --wf-bottom-nav-offset is
 * derived on :root (globals.css).
 */
function applyDeviceMode(mode: DeviceMode, layout: UiLayout): void {
  document.body.dataset.mode = mode;
  document.documentElement.style.setProperty('--wf-bottom-nav-h', layout === 'compact' ? '3.5rem' : '0px');
}

/**
 * Pre-render boot (main.tsx): device class, `--k-scale` and data-mode from the initial surface
 * (`?surface=`, the cached kiosk info, VITE_DOMAIN_TYPE), so the first paint already has the
 * right size (no 1.0 → 1.5 jump on a kiosk).
 */
export function bootDeviceMode(initial: SurfaceResolution = { surface: 'unknown', source: 'none' }): void {
  if (typeof document === 'undefined') return;
  const search = readSearch();
  const input: DeviceInput = { ...readViewport(), kioskDomain: isKioskHardware(initial.surface, initial.source) };
  const deviceOverride = readDeviceOverride(search, isDeviceOverrideAllowed());
  const scaleOverride = readScaleOverride(search, safeLocalStorage(), scaleOverrideAllowedFor(input, deviceOverride));
  const ui = computeUiScale(input, { scaleOverride, deviceOverride });
  applyUiScale(ui, document.documentElement, document.body);
  applyDeviceMode(deviceModeFor(ui.device), ui.layout);
}

/**
 * Source of truth for the device class and UI scale (src/config/uiScale.ts):
 * - `kiosk`: the kiosk surface (useSurface: kiosk when the server, its cached answer or the build
 *   flag says kiosk, or the dev/preview `?surface=`), and not a Pages preview host unless
 *   `?surface=kiosk` asked. Checked first: a kiosk under Windows display scaling can be < 768px.
 * - `phone`: narrow viewport (<768px) or a phone on "Desktop site", on every other surface.
 * - `tablet`: primary pointer is a finger (`pointer: coarse`), short side ≥ 600, long side ≤ 1400.
 * - `desktop`: everything else.
 *
 * Writes `--k-scale` and `--wf-bottom-nav-h` on <html>, `data-device` / `data-orientation` /
 * `data-layout` on <body>, and the legacy `body[data-mode]` (phone → mobile, kiosk → kiosk,
 * tablet and desktop → desktop).
 * Recomputes on resize, rotation and when the surface resolves; only window/screen sizes feed it, so a
 * font-size change never triggers another pass. The on-screen keyboard (window height only)
 * never changes the class: the phone and pointer readings are kept (nextViewportReading).
 * `?ui-scale=` applies only on the kiosk class or in a dev/preview build (isScaleOverrideAllowed).
 */
export function DeviceModeProvider({ children }: { children: React.ReactNode }) {
  const { surface, source } = useSurface();
  const [search] = useState(readSearch);
  const [deviceOverride] = useState(() => readDeviceOverride(search, isDeviceOverrideAllowed()));
  const [viewport, setViewport] = useState(readViewport);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH - 1}px)`);
    const pointerMq = window.matchMedia(COARSE_POINTER_QUERY);
    const orientation = typeof screen !== 'undefined' ? screen.orientation : undefined;
    const update = () => setViewport((prev) => nextViewportReading(prev, readViewport()));
    update();
    mq.addEventListener('change', update);
    pointerMq.addEventListener?.('change', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    orientation?.addEventListener?.('change', update);
    return () => {
      mq.removeEventListener('change', update);
      pointerMq.removeEventListener?.('change', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      orientation?.removeEventListener?.('change', update);
    };
  }, []);

  const kioskDomain = isKioskHardware(surface, source);

  const input = useMemo<DeviceInput>(() => ({ ...viewport, kioskDomain }), [viewport, kioskDomain]);
  // Only a change of whether the override is allowed re-reads it (e.g. the kiosk domain arrives
  // after the first paint); reading again with the same URL is idempotent.
  const scaleOverrideAllowed = scaleOverrideAllowedFor(input, deviceOverride);
  const scaleOverride = useMemo(
    () => readScaleOverride(search, safeLocalStorage(), scaleOverrideAllowed),
    [search, scaleOverrideAllowed]
  );
  const ui = useMemo(
    () => computeUiScale(input, { scaleOverride, deviceOverride }),
    [input, scaleOverride, deviceOverride]
  );
  const mode = deviceModeFor(ui.device);
  const layout = ui.layout;

  useLayoutEffect(() => {
    applyUiScale(ui, document.documentElement, document.body);
  }, [ui]);

  // Layout effect: on rotation the bar height changes in the same frame as BottomNav (no flash).
  useLayoutEffect(() => {
    applyDeviceMode(mode, layout);
    return () => {
      delete document.body.dataset.mode;
      document.documentElement.style.removeProperty('--wf-bottom-nav-h');
    };
  }, [mode, layout]);

  return (
    <DeviceModeContext.Provider value={mode}>
      <UiScaleContext.Provider value={ui}>{children}</UiScaleContext.Provider>
    </DeviceModeContext.Provider>
  );
}

export function useDeviceMode(): DeviceMode {
  const ctx = useContext(DeviceModeContext);
  if (!ctx) throw new Error('useDeviceMode must be used inside <DeviceModeProvider>');
  return ctx;
}

/** Device class, orientation, scale, design width and height (viewport ÷ scale) and compact/wide layout. */
export function useUiScale(): UiScaleState {
  const ctx = useContext(UiScaleContext);
  if (!ctx) throw new Error('useUiScale must be used inside <DeviceModeProvider>');
  return ctx;
}
